import React, { useEffect, useState, useRef } from "react";
import { Tree } from "../../../lib/useTrees";
import { ComponentProps } from "../../../types";
import { SectionWrapper } from "../../containers/SectionWrapper";
import * as styles from "./TreeListing.module.css";
import { TreeStamp } from "../../elements/TreeStamp/TreeStamp";
import Cookies from "js-cookie";

interface Props extends ComponentProps {
  trees: Tree[];
}

export const TreeListing: React.FC<Props> = ({ trees, ...props }) => {
  const pageCount = Math.ceil(trees.length / 16);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleTrees, setVisibleTrees] = useState(trees.slice(0, 16));
  const treesContainer = useRef(null);
  const treeListingCookieName = `treeListing${props.id}`;

  const handlePageChange = (direction: "fwd" | "back") => {
    if (direction === "back" && currentPage !== 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      Cookies.set(treeListingCookieName, `${newPage}`);
    }

    if (direction === "fwd" && currentPage < pageCount) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      Cookies.set(treeListingCookieName, `${newPage}`);
    }

    if (treesContainer?.current && typeof window !== undefined) {
      window?.scrollTo({
        top: treesContainer?.current?.offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const firstTree = (currentPage - 1) * 16;
    const lastTree = firstTree + 16;

    setVisibleTrees(trees.slice(firstTree, lastTree));
  }, [currentPage]);

  useEffect(() => {
    const storedPage = Cookies.get(treeListingCookieName);
    storedPage && setCurrentPage(parseInt(storedPage));
  }, []);

  return (
    <SectionWrapper {...props}>
      <div className={styles.treelisting} ref={treesContainer}>
        {pageCount > 1 && (
          <div className={styles.progress}>
            <span className={styles.totalPages}>{pageCount} pages</span> |
            Showing page {currentPage} ({visibleTrees[0].commonName} -{" "}
            {visibleTrees[visibleTrees.length - 1].commonName})
          </div>
        )}
        <div className={styles.treesContainer}>
          {visibleTrees.map((tree, idx) => {
            if (!tree.commonName) return null;

            return (
              <TreeStamp
                name={tree.commonName}
                treeId={tree.treeId}
                image={tree.image}
                species={tree.scientificName}
                cta={
                  tree?.page?.url
                    ? {
                        label: "View Details",
                        href: tree.page.url,
                        target: "_self",
                      }
                    : undefined
                }
                clickable={true}
                style="listing"
                key={`treeStamp-${idx}`}
              />
            );
          })}
        </div>
        {pageCount > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.backBtn}
              onClick={(event) => handlePageChange("back")}
              disabled={currentPage === 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10.573"
                height="18.501"
                data-name="211647_right_chevron_icon"
              >
                <path
                  fill="#296348"
                  d="M7.035 9.248.128 2.055a.47.47 0 0 1 .008-.653L1.371.138a.448.448 0 0 1 .64-.008l8.436 8.787a.431.431 0 0 1 .124.335.457.457 0 0 1-.124.335L2.011 18.37a.448.448 0 0 1-.64-.008L.136 17.098a.47.47 0 0 1-.008-.653Z"
                  data-name="211647_right_chevron_icon"
                />
              </svg>{" "}
            </button>{" "}
            <p>
              <strong>{currentPage}</strong> of {pageCount}
            </p>
            <button
              onClick={(event) => handlePageChange("fwd")}
              disabled={currentPage === pageCount}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10.573"
                height="18.501"
                data-name="211647_right_chevron_icon"
              >
                <path
                  d="M7.035 9.248.128 2.055a.47.47 0 0 1 .008-.653L1.371.138a.448.448 0 0 1 .64-.008l8.436 8.787a.431.431 0 0 1 .124.335.457.457 0 0 1-.124.335L2.011 18.37a.448.448 0 0 1-.64-.008L.136 17.098a.47.47 0 0 1-.008-.653Z"
                  data-name="211647_right_chevron_icon"
                />
              </svg>{" "}
            </button>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};
